import React from "react";
import { Column } from "primereact/column";
import { FilterMatchMode } from "primereact/api";
import { DataTable } from "primereact/datatable";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import { useTranslation } from "react-i18next";
import { styled } from "@mui/material/styles";
import { DomainMappingTableEntry } from "./DomainMappingTableEntry";
import { DomainMappingHeaderToolbar } from "./DomainMappingTableHeaderToolbar";
import { InputText } from "primereact/inputtext";

interface CustomerSettingsDomainMappingsTableProps {
  domainMappingTableEntries: DomainMappingTableEntry[];
  onUpdatedDomainMappingTableEntries: (domainMappingTableEntries: DomainMappingTableEntry[]) => void;
  onAddDomainClick: () => void;
  loading: boolean;
  isEdit: boolean;
}

const StyledDiv = styled("div")`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
`;

export function DomainMappingTable(props: CustomerSettingsDomainMappingsTableProps) {
  const { t } = useTranslation();
  const { domainMappingTableEntries, onUpdatedDomainMappingTableEntries, onAddDomainClick, isEdit } = props;

  const updateUserEntryDomain = (rowId: string, domain: string): void => {
    const newDomainMappingTableEntries = domainMappingTableEntries.map((domainMappingTableEntry) => {
      if (domainMappingTableEntry.rowId === rowId) {
        return {
          ...domainMappingTableEntry,
          domain: domain,
        };
      }
      return domainMappingTableEntry;
    });

    onUpdatedDomainMappingTableEntries(newDomainMappingTableEntries);
  };

  function handleDelete(rowData: DomainMappingTableEntry) {
    const rowDeleted = domainMappingTableEntries.filter((entry) => entry.rowId !== rowData.rowId);
    onUpdatedDomainMappingTableEntries(rowDeleted);
  }

  function handleAddDomainClick() {
    onAddDomainClick();
  }

  return (
    <DataTable
      header={<DomainMappingHeaderToolbar onAddDomainClick={handleAddDomainClick} isEdit={isEdit} />}
      value={domainMappingTableEntries}
      loading={props.loading}
      paginator
      rows={10}
    >
      <Column
        field="customer"
        header={`${t("customer.customer")}`}
        sortable
        filter
        filterMatchMode={FilterMatchMode.CONTAINS}
        body={(rowData) => {
          return <StyledDiv>{rowData?.customerName}</StyledDiv>;
        }}
      />
      <Column
        field="domain"
        header={`${t("settings.domain")} ${t("common.required")}`}
        sortable
        filter
        filterMatchMode={FilterMatchMode.CONTAINS}
        body={(rowData) => {
          return (
            <StyledDiv>
              <InputText
                value={rowData.domain}
                disabled={!isEdit}
                onChange={(e) => updateUserEntryDomain(rowData.rowId, e.currentTarget.value)}
              />
            </StyledDiv>
          );
        }}
      />
      <Column
        header={t("common.delete")}
        body={(rowData: DomainMappingTableEntry) => {
          return (
            <>
              {isEdit && (
                <StyledDiv style={{ display: "flex", justifyContent: "center" }}>
                  <Tooltip title={<div>{t("common.delete")}</div>}>
                    <span>
                      <IconButton onClick={() => handleDelete(rowData)} size="large">
                        <DeleteForeverIcon color={"error"} />
                      </IconButton>
                    </span>
                  </Tooltip>
                </StyledDiv>
              )}
            </>
          );
        }}
      />
    </DataTable>
  );
}
